/**
 * Phase G — Mishap Corrector public route (/mishap-corrector).
 * Wizard: Program → Upload/Manual → Confirm → Contact → Results.
 * Palette: ivy / brass / bone / crimson-for-alerts only.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { faqJsonLd, MISHAP_FAQ } from "@/lib/faqSchemas";
import MishapResultsView from "@/components/MishapResultsView";

const STEPS = ["Program", "Transcript", "Confirm", "Contact", "Results"];

const PROGRAMS = [
  { slug: "uf-dietetics",  label: "UF Dietetics (Warrington-track BS)",         dest: "University of Florida",           blurb: "Competitive DPD; requires 3.2 prereq GPA + B in critical-tracking courses." },
  { slug: "uf-business",   label: "UF Business Administration (BABA)",          dest: "University of Florida",           blurb: "Warrington 7-course critical tracking; C-or-better in every prereq." },
  { slug: "uf-prehealth",  label: "UF Pre-Health (advising track)",             dest: "University of Florida",           blurb: "Not a major — advising track. Beyond Valencia recommends pairing with a matching BS." },
  { slug: "ucf-business",  label: "UCF Business Admin (BSBA via DirectConnect)", dest: "University of Central Florida",   blurb: "DirectConnect guarantees UCF admission via AA; BSBA still requires prereqs." },
  { slug: "ucf-prehealth", label: "UCF Pre-Health (advising track)",            dest: "University of Central Florida",   blurb: "Pre-health science track — pair with Biology/Biomed BS at UCF." },
];

const LS_KEY = "bv_mishap_program_slug";
const GRADES = ["A","A-","B+","B","B-","C+","C","C-","D+","D","D-","F","W","I","P"];

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="mishap-step-dots">
      {STEPS.map((label, i) => {
        const active = i <= step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 border ${active ? "bg-ivy text-bone border-ivy" : "bg-bone text-stone border-stone/40"} text-[10px] font-semibold flex items-center justify-center tabular-nums`}>{i + 1}</div>
            <div className={`text-[10px] uppercase tracking-[0.22em] ${active ? "text-ivy font-semibold" : "text-stone"}`}>{label}</div>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-stone/40" />}
          </div>
        );
      })}
    </div>
  );
}

export default function MishapCorrector() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [programSlug, setProgramSlug] = useState(() => localStorage.getItem(LS_KEY) || "uf-dietetics");
  const [mode, setMode] = useState(searchParams.get("manual") === "1" ? "manual" : "upload");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parse, setParse] = useState(null);
  const [editedCourses, setEditedCourses] = useState([]);
  const [manualRows, setManualRows] = useState([
    { course_code: "", title: "", term: "Fall 2024", grade: "A", credits: 3 },
  ]);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => { localStorage.setItem(LS_KEY, programSlug); }, [programSlug]);
  useEffect(() => { if (searchParams.get("manual") === "1") { setMode("manual"); if (step < 1) setStep(1); } }, [searchParams, step]);

  const program = PROGRAMS.find((p) => p.slug === programSlug) || PROGRAMS[0];

  const doUpload = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error("Please attach a file."); return; }
    const okExt = /\.(pdf|jpg|jpeg|png)$/i.test(file.name);
    if (!okExt) { toast.error("PDF, JPG or PNG only."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (10 MB max)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target_program_slug", programSlug);
      fd.append("source_institution_slug", "valencia-college");
      const r = await api.post("/mishap/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParse(r.data);
      setEditedCourses(r.data.parsed_courses || []);
      setStep(2);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.error === "text_layer_missing" || detail.error === "text_layer_missing_ocr_failed")) {
        toast.error(detail.message || "Automated parse failed — switch to manual entry.");
        setMode("manual");
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed.");
      }
    } finally { setUploading(false); }
  };

  const doManualSubmit = async (e) => {
    e?.preventDefault?.();
    const valid = manualRows.filter((r) => r.course_code && /^[A-Z]{3,4}\s?\d{4}[A-Z]?$/.test(r.course_code.toUpperCase().trim()));
    if (valid.length === 0) { toast.error("Add at least one row with a valid course code."); return; }
    setUploading(true);
    try {
      const r = await api.post("/mishap/manual", {
        target_program_slug: programSlug,
        source_institution_slug: "valencia-college",
        courses: valid.map((row) => ({
          course_code: row.course_code.toUpperCase().trim(),
          title: row.title || "",
          term: row.term || "",
          grade: row.grade || "A",
          credits: Number(row.credits) || 3,
        })),
      });
      setParse(r.data);
      setEditedCourses(r.data.parsed_courses || []);
      setStep(2);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save manual entry.");
    } finally { setUploading(false); }
  };

  const doAnalyze = async (e) => {
    e?.preventDefault?.();
    if (!firstName.trim() || !email.trim()) { toast.error("First name and email required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email."); return; }
    setAnalyzing(true);
    try {
      const r = await api.post("/mishap/analyze", {
        transcript_id: parse.transcript_id,
        email: email.trim(),
        first_name: firstName.trim(),
        parsed_courses_override: editedCourses,
      });
      setAnalysis(r.data);
      setStep(4);
      window.history.replaceState({}, "", `/mishap-corrector/results/${r.data.analysis_id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Analysis failed.");
    } finally { setAnalyzing(false); }
  };

  const editRow = (i, field, value) => setEditedCourses((prev) => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  const editManualRow = (i, field, value) => setManualRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const addManualRow = () => setManualRows((prev) => [...prev, { course_code: "", title: "", term: "Fall 2024", grade: "A", credits: 3 }]);
  const delManualRow = (i) => setManualRows((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="min-h-screen bg-bone">
      <SEO title="Mishap Corrector — fix your Valencia transfer path | Beyond Valencia" description="Upload your Valencia transcript. Get a real, sourced transfer-path correction in 3 minutes." canonical="/mishap-corrector" jsonLd={faqJsonLd(MISHAP_FAQ)} />
      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="mishap-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Free · No signup · Real sources</div>
          <h1 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Fix your transfer path in 3 minutes.</h1>
          <p className="mt-5 text-lg text-bone-dim max-w-2xl leading-relaxed">Pick your target program. Upload your Valencia transcript (or enter courses manually). We check every course against the official prereqs and give you the exact term-by-term correction — with sources on every claim.</p>
          <div className="mt-8"><StepDots step={step} /></div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
        {/* STEP 0 — PROGRAM PICKER */}
        {step === 0 && (
          <div className="space-y-6" data-testid="mishap-step-program">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Step 1</div>
              <h2 className="font-serif text-3xl text-ink mt-2 tracking-tight">Pick your target program</h2>
              <p className="mt-2 text-sm text-ink-soft max-w-2xl">We&apos;ll evaluate your transcript against the official published prereqs for this program.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-0 border-t border-l border-stone/40">
              {PROGRAMS.map((p) => {
                const active = programSlug === p.slug;
                return (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => setProgramSlug(p.slug)}
                    className={`border-r border-b border-stone/40 text-left p-5 transition-colors ${active ? "bg-ivy text-bone" : "bg-bone hover:bg-bone-dim"}`}
                    data-testid={`mishap-program-${p.slug}`}
                  >
                    <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${active ? "text-brass-soft" : "text-brass"}`}>{p.dest}</div>
                    <div className={`mt-2 font-serif text-lg tracking-tight ${active ? "text-bone" : "text-ink"}`}>{p.label}</div>
                    <div className={`mt-2 text-xs leading-relaxed ${active ? "text-bone-dim" : "text-ink-soft"}`}>{p.blurb}</div>
                  </button>
                );
              })}
              {/* Phase L — 6th option: unsupported program → shorter intake */}
              <button
                type="button"
                onClick={() => (window.location.href = "/mishap-corrector/unsupported")}
                className="border-r border-b border-stone/40 text-left p-5 transition-colors bg-bone hover:bg-bone-dim md:col-span-2"
                data-testid="mishap-program-unsupported"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brass">My target program isn&apos;t listed →</div>
                <div className="mt-2 font-serif text-lg tracking-tight text-ink">Tell us where you&apos;re trying to transfer</div>
                <div className="mt-2 text-xs leading-relaxed text-ink-soft">A shorter intake for any US destination. Ivy / elite / out-of-state targets are routed to our partner Ivy League Consulting.</div>
              </button>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setStep(1)} className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8" data-testid="mishap-program-continue">Continue →</button>
            </div>
          </div>
        )}

        {/* STEP 1 — UPLOAD OR MANUAL */}
        {step === 1 && (
          <div className="space-y-6" data-testid="mishap-step-upload">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Step 2 · Analyzing against</div>
              <h2 className="font-serif text-3xl text-ink mt-1 tracking-tight">{program.label}</h2>
              <a href={program.slug ? `#` : "#"} onClick={(e) => e.preventDefault()} className="text-[11px] uppercase tracking-[0.22em] text-brass">{program.dest}</a>
            </div>
            <div className="flex border border-stone/40 w-fit" data-testid="mishap-mode-toggle">
              {[["upload","Upload PDF or image"],["manual","Enter courses manually"]].map(([v,l]) => (
                <button key={v} onClick={() => setMode(v)} className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2 ${mode===v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid={`mishap-mode-${v}`}>{l}</button>
              ))}
            </div>
            {mode === "upload" ? (
              <form onSubmit={doUpload} className="space-y-6">
                <p className="text-sm text-ink-soft max-w-xl">PDF, JPG or PNG. Text-layer PDFs parse fastest — scanned/photo transcripts go through OCR. Never shared. Delete anytime.</p>
                <label className="block border border-stone/40 bg-bone-dim p-8 cursor-pointer hover:border-ivy transition-colors" data-testid="mishap-drop-zone">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mishap-file-input" />
                  <div className="text-center">
                    <div className="text-brass text-[10px] uppercase tracking-[0.22em]">Click to select file</div>
                    <div className="mt-2 font-serif text-lg text-ink">{file ? file.name : "Choose your transcript file"}</div>
                    <div className="mt-1 text-xs text-stone">PDF / JPG / PNG · 10 MB max · OCR supported</div>
                  </div>
                </label>
                <div className="flex justify-between">
                  <button type="button" onClick={() => setStep(0)} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy">← Back</button>
                  <button type="submit" disabled={!file || uploading} className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 disabled:opacity-50" data-testid="mishap-upload-btn">{uploading ? "Parsing…" : "Parse transcript"}</button>
                </div>
              </form>
            ) : (
              <form onSubmit={doManualSubmit} className="space-y-6" data-testid="mishap-step-manual">
                <p className="text-sm text-ink-soft">Add each Valencia course you&apos;ve taken. Course codes follow the Florida SCNS format (e.g. <span className="font-mono">BSC 1010C</span>, <span className="font-mono">ACG 2021</span>).</p>
                <div className="border-t border-l border-stone/40" data-testid="mishap-manual-table">
                  <div className="grid grid-cols-12 border-r border-b border-stone/40 bg-bone-dim/50 text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">
                    <div className="col-span-2 px-3 py-2">Code</div>
                    <div className="col-span-4 px-3 py-2">Title</div>
                    <div className="col-span-2 px-3 py-2">Term</div>
                    <div className="col-span-1 px-3 py-2">Grade</div>
                    <div className="col-span-2 px-3 py-2">Credits</div>
                    <div className="col-span-1 px-3 py-2"></div>
                  </div>
                  {manualRows.map((row, i) => (
                    <div key={i} className="grid grid-cols-12 border-r border-b border-stone/40 bg-bone items-center" data-testid={`mishap-manual-row-${i}`}>
                      <input className="col-span-2 px-3 py-2 bg-transparent text-sm text-ink font-mono focus:outline-none focus:bg-brass/10" value={row.course_code} placeholder="BSC 1010C" onChange={(e) => editManualRow(i, "course_code", e.target.value.toUpperCase())} data-testid={`mishap-manual-code-${i}`} />
                      <input className="col-span-4 px-3 py-2 bg-transparent text-sm text-ink focus:outline-none focus:bg-brass/10" value={row.title} placeholder="Course title" onChange={(e) => editManualRow(i, "title", e.target.value)} />
                      <input className="col-span-2 px-3 py-2 bg-transparent text-sm text-stone focus:outline-none focus:bg-brass/10" value={row.term} placeholder="Fall 2024" onChange={(e) => editManualRow(i, "term", e.target.value)} />
                      <select className="col-span-1 px-2 py-2 bg-transparent text-sm text-ink font-semibold focus:outline-none focus:bg-brass/10" value={row.grade} onChange={(e) => editManualRow(i, "grade", e.target.value)}>
                        {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <input type="number" step="0.5" min="0.5" max="6" className="col-span-2 px-3 py-2 bg-transparent text-sm text-ink tabular-nums focus:outline-none focus:bg-brass/10" value={row.credits} onChange={(e) => editManualRow(i, "credits", e.target.value)} />
                      <button type="button" onClick={() => delManualRow(i)} className="col-span-1 text-stone hover:text-crimson text-xs" data-testid={`mishap-manual-del-${i}`} disabled={manualRows.length === 1}>Remove</button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addManualRow} className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass" data-testid="mishap-manual-add-row">+ Add row</button>
                <div className="flex justify-between">
                  <button type="button" onClick={() => setStep(0)} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy">← Back</button>
                  <button type="submit" disabled={uploading} className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 disabled:opacity-50" data-testid="mishap-manual-submit">{uploading ? "Saving…" : "Save & continue"}</button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* STEP 2 — CONFIRM PARSE */}
        {step === 2 && parse && (
          <div className="space-y-6" data-testid="mishap-step-confirm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Step 3 · Analyzing against</div>
              <h2 className="font-serif text-3xl text-ink mt-1 tracking-tight">{program.label}</h2>
              <p className="mt-2 text-sm text-ink-soft max-w-2xl">Edit any misparsed row inline. This does not save until you hit Analyze.</p>
            </div>
            {parse.parse_confidence < 0.7 && (<div className="border border-stone/40 bg-bone-dim px-4 py-3 text-[12px] text-ink" data-testid="mishap-low-confidence">Parse confidence {Math.round(parse.parse_confidence * 100)}% — please double-check every row.</div>)}
            {(parse.warnings || []).map((w, i) => (<div key={i} className="border border-brass/50 bg-brass/10 px-4 py-3 text-[12px] text-ink">{w}</div>))}
            <div className="border-t border-l border-stone/40" data-testid="mishap-parse-table">
              <div className="grid grid-cols-12 border-r border-b border-stone/40 bg-bone-dim/50 text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">
                <div className="col-span-2 px-3 py-2">Code</div><div className="col-span-5 px-3 py-2">Title</div><div className="col-span-2 px-3 py-2">Term</div><div className="col-span-1 px-3 py-2">Grade</div><div className="col-span-2 px-3 py-2">Credits</div>
              </div>
              {editedCourses.map((c, i) => (
                <div key={i} className="grid grid-cols-12 border-r border-b border-stone/40 bg-bone" data-testid={`mishap-parse-row-${i}`}>
                  <input className="col-span-2 px-3 py-2 bg-transparent text-sm text-ink font-mono focus:outline-none focus:bg-brass/10" value={c.course_code || ""} onChange={(e) => editRow(i,"course_code",e.target.value.toUpperCase())} />
                  <input className="col-span-5 px-3 py-2 bg-transparent text-sm text-ink focus:outline-none focus:bg-brass/10" value={c.title || ""} onChange={(e) => editRow(i,"title",e.target.value)} />
                  <input className="col-span-2 px-3 py-2 bg-transparent text-sm text-stone focus:outline-none focus:bg-brass/10" value={c.term || ""} onChange={(e) => editRow(i,"term",e.target.value)} />
                  <input className="col-span-1 px-3 py-2 bg-transparent text-sm text-ink font-semibold focus:outline-none focus:bg-brass/10" value={c.grade || ""} onChange={(e) => editRow(i,"grade",e.target.value.toUpperCase())} />
                  <input className="col-span-2 px-3 py-2 bg-transparent text-sm text-ink tabular-nums focus:outline-none focus:bg-brass/10" value={c.credits ?? ""} onChange={(e) => editRow(i,"credits", parseFloat(e.target.value) || 0)} />
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy" data-testid="mishap-back-to-upload">← Back</button>
              <button onClick={() => setStep(3)} disabled={editedCourses.length === 0} className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 disabled:opacity-50" data-testid="mishap-continue-to-contact">Looks right — continue</button>
            </div>
          </div>
        )}

        {/* STEP 3 — CONTACT */}
        {step === 3 && (
          <form onSubmit={doAnalyze} className="space-y-6 max-w-lg" data-testid="mishap-step-contact">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Step 4</div>
              <h2 className="font-serif text-3xl text-ink mt-2 tracking-tight">Where should we send the analysis?</h2>
              <p className="mt-2 text-sm text-ink-soft">Free. No credit card. We&apos;ll email you the persistent link — and a Beyond Valencia adviser can help you turn the corrections into a plan.</p>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[11px] uppercase tracking-[0.22em] text-stone mb-2">First name</label><input className="w-full bg-bone border border-stone/40 px-3 py-3 text-sm text-ink focus:outline-none focus:border-ivy" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="mishap-first-name" /></div>
              <div><label className="block text-[11px] uppercase tracking-[0.22em] text-stone mb-2">Email</label><input type="email" className="w-full bg-bone border border-stone/40 px-3 py-3 text-sm text-ink focus:outline-none focus:border-ivy" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="mishap-email" /></div>
            </div>
            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(2)} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy">← Back</button>
              <button type="submit" disabled={analyzing} className="bg-brass hover:bg-brass/90 text-ivy uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 disabled:opacity-50" data-testid="mishap-analyze-btn">{analyzing ? "Analyzing…" : "Analyze my path"}</button>
            </div>
          </form>
        )}

        {/* STEP 4 — RESULTS */}
        {step === 4 && analysis && (
          <div>
            <div className="mb-6 text-[11px] uppercase tracking-[0.22em] text-brass" data-testid="mishap-analyzed-against">
              Analyzed against · <span className="text-ivy font-semibold">{program.label}</span>
            </div>
            <MishapResultsView analysis={analysis} showPrintCTA={true} />
          </div>
        )}
      </section>
    </div>
  );
}
